import React, { useEffect } from 'react';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import DOMPurify from 'dompurify';
import clsx from 'clsx';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import {
  getInternationalizedResource,
  getDefaultLanguageResource,
  setDefaultLanguage,
  changeFavicon,
} from '../../shared/utils/i18nUtils';
import { resourceKeys } from '../../shared/enums/resourceKeys';
import { postSignInThunk } from '../../redux/features/auth/auth.thunk';

const useStyles = props =>
  makeStyles(theme => ({
    landingPageContainer: {
      height: '38rem',
      backgroundColor: theme.palette.landingBackground,
    },
    landingPageContainerV2: {
      height: 'inherit',
    },
    '@global': {
      '*': {
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      },
      body: {
        fontFamily: theme.typography.fontFamily,
      },
      '.topnav': {
        display: 'flex',
        width: '100%',
        height: '5.125rem',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '0 100px',
        backgroundColor: theme.palette.white,
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
        '& .logo': {
          width: props?.logoWidth || theme.logoWidth,
          height: theme.logoHeight,
          backgroundImage: `url(${theme.logoInner})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '100%',
          '&.logo-xl': {
            marginTop: '10px',
            marginBottom: '10px',
          },
        },
        [theme.breakpoints.down('md')]: {
          backgroundImage: () => theme.logoInnerMobile && `url(${theme.logoInner})`,
        },
        '& a': {
          cursor: 'pointer',
        },
        '& .vertical-line': {
          borderLeft: `1px solid ${theme.palette.headerLogoSeparator}`,
          height: '55px',
          margin: '0 1.5rem',
        },
      },
      '.container': {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      '.card': {
        backgroundColor: theme.palette.white,
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
        borderRadius: '5px',
        transition: '0.3s',
        width: '500px',

        padding: '1rem',
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
        [theme.breakpoints.up('sm')]: {
          padding: '2rem',
        },
        [theme.breakpoints.up('md')]: {
          padding: '3.75rem',
        },
        [theme.breakpoints.up('lg')]: {
          marginTop: '1.625rem',
        },
        '& .login-button': {
          fontWeight: '600',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
          '&:hover': {
            color: theme.palette.primary.hoverContrastText,
            backgroundColor: theme.palette.primary.hover,
          },
          '&:focus, &:active': {
            color: theme.palette.primary.activeText,
            backgroundColor: theme.palette.primary.active,
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
          },
          '&:disabled': {
            color: theme.palette.primary.disabledText,
            backgroundColor: theme.palette.primary.disabled,
          },
          width: '100%',
          padding: '0.625rem 0.875rem',
          border: 'none',
          borderRadius: '0.25rem',
          marginBottom: '1.5rem',
          display: 'inline-block',
          fontSize: '1rem',
          lineHeight: '1.5rem',
          textDecoration: 'none',
          fontFamily: theme.typography.fontFamily,
          cursor: 'pointer',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
        },
        '& .login-button.login-button-inverted': {
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          '&:hover': {
            color: theme.palette.secondary.hoverContrastText,
            backgroundColor: theme.palette.secondary.hover,
          },
          '&:focus, &:active': {
            backgroundColor: theme.palette.secondary.main,
          },
          '&:disabled, &.Mui-disabled': {
            color: theme.palette.secondary.disabledText,
            backgroundColor: theme.palette.secondary.disabled,
          },
        },
        '& .divider': {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: theme.palette.disabled,
          marginBottom: '1.5rem',
          '& .divider-line': {
            margin: '0.5rem 0',
            height: '1px',
            width: '100%',
            backgroundColor: theme.palette.lineDivider,
          },
          '& span': {
            margin: '0 1rem',
          },
        },
        '&:hover': {
          boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.2)',
        },
      },
      '.row': {
        display: 'flex',
        maxWidth: '1240px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '54px',
        },
        [theme.breakpoints.up('lg')]: {
          padding: '0 1.5rem',
          flexDirection: 'row',
          alignItems: 'flex-start',
        },
      },
      '.column': {
        flex: '50%',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        padding: '0 1.5rem',
        [theme.breakpoints.down('md')]: {
          maxWidth: '500px',
        },
        [theme.breakpoints.up('md')]: {
          padding: 0,
        },
      },
      '#title': {
        display: 'flex',
        flexDirection: 'column',
        padding: '2.125rem 0 0.438rem 0',
        [theme.breakpoints.up('md')]: {
          padding: '3.375rem 0',
        },
        [theme.breakpoints.up('lg')]: {
          padding: '0 1.5rem',
        },
      },
      '.landing-title': {
        fontSize: '1.75rem',
        lineHeight: '2.625rem',
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600,
        margin: 0,
        '&.landing-title-sec': {
          marginTop: '3rem',
        },
        '& br': {
          display: 'none',
        },
        [theme.breakpoints.up('md')]: {
          fontSize: '1.938rem',
          lineHeight: '2.813rem',
          marginBottom: '0.875rem',
        },
        [theme.breakpoints.up('lg')]: {
          fontSize: '2.75rem',
          lineHeight: '4.5rem',
          marginBottom: '2.125rem',
          '& br': {
            display: 'initial',
          },
        },
        [theme.breakpoints.up('xl')]: {
          fontSize: '3.625rem',
          marginBottom: '3.563rem',
        },
        [theme.breakpoints.up('xl2')]: {
          fontSize: '3.875rem',
        },
        [theme.breakpoints.up('xl3')]: {
          fontSize: '4.5rem',
        },
      },
      '.subtitle': {
        fontSize: '1rem',
        lineHeight: '1.5rem',
        [theme.breakpoints.up('md')]: {
          fontSize: '1.125rem',
        },
        [theme.breakpoints.up('xl')]: {
          fontSize: '1.375rem',
        },
        fontWeight: '400',
        paddingBottom: '24px',
      },
      '.get-one': {
        color: '#3D5682',
      },
      '.epo-card-info': {
        paddingTop: '1rem',

        '& .link-container': {
          marginTop: '0.75rem',
          '& .psw-link > a': {
            color: theme.palette.linkColour,
            fontWeight: 600,
            '&:visited': {
              color: `${theme.palette.visitedLink}`,
            },
          },
        },
      },
      '.footer': {
        boxShadow: '0 0.5rem 1rem 0 rgba(0, 0, 0, 0.2)',
        height: '50px',
        backgroundColor: theme.palette.white,
        width: '100%',
        borderTop: `1px solid ${theme.palette.primary.border}`,
        background: theme.palette.white,
        '& p': {
          color: theme.palette.black,

          padding: '0.625rem 0.875rem',
          [theme.breakpoints.up('xl')]: {
            marginLeft: '145px',
          },
        },
      },
    },
  }));

export default function LandingPage() {
  const dispatch = useDispatch();
  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const frontendConfig = frontendCustomisation.resource;
  const landingPageConfig = frontendCustomisation.resource?.landingPage;
  const LOGO_WIDTH = frontendConfig?.logoWidth;
  const DEFAULT_LANGUAGE = frontendConfig?.languages?.default;
  const classes = useStyles({
    logoWidth: LOGO_WIDTH,
  })();

  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);

  const configResource =
    ConfigResourceProps[getInternationalizedResource(resourceKeys.LANDING_PAGE.RESOURCE_KEY)] ||
    ConfigResourceProps[getDefaultLanguageResource(resourceKeys.LANDING_PAGE.RESOURCE_KEY)];
  const landingPageConfiguration = useConfiguration(configResource);

  const location = useLocation();
  const _search = qs.parse(location.search);

  useEffect(() => {
    if (DEFAULT_LANGUAGE) {
      setDefaultLanguage(DEFAULT_LANGUAGE);
    }
    if (_search?.login === 'action-redirect_eposc') {
      dispatch(postSignInThunk({ isCertificate: true }));
    }
    changeFavicon(landingPageConfig?.favicon || '/favicon.ico');
  }, []);

  const redirectToSmartCardLogin = () => {
    const queryParams = qs.stringify({ login: 'action-redirect_eposc' });
    let domainPrefix = `${globalConfiguration.resource?.loginPage?.clientCertificateDomainPrefix}`;
    if (`${window.location.hostname}`.startsWith(domainPrefix)) {
      domainPrefix = '';
    }
    const url = `${window.location.protocol}//${domainPrefix}${window.location.hostname}?${queryParams}`;
    window.location.assign(url);
  };

  if (_search?.login === 'action-redirect_eposc') {
    return <div />;
  }

  return (
    <div data-testid="landing-page-wrapper" role="main">
      <div
        className={clsx(
          classes.landingPageContainer,
          landingPageConfig?.fullHeight ? classes.landingPageContainerV2 : null,
        )}
        data-testId="landing-page"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(landingPageConfiguration.resource) }}
        onClick={(event) => {
          if (event.target?.id === 'action-redirect_eposc') {
            redirectToSmartCardLogin();
          } else if (event.target?.id.startsWith('action-redirect_')) {
            const buttonId = event.target?.id;
            const idpHint = buttonId.substr('action-redirect_'.length, buttonId.length);
            dispatch(postSignInThunk({ idpHint }));
          }
        }}
      />
    </div>
  );
}
